import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { logAudit } from '../lib/audit';
import { getFreshToken, forceReauth } from '../lib/session';
import { FileText, ClipboardList, ClipboardCheck, Receipt, Contact, Package, CreditCard, Settings, LogOut, Headset, Bell, ChevronDown, ShieldAlert, Zap, Truck, Calculator, Building2, FileStack, Globe, BarChart3, MoreHorizontal, Menu, X, Eye, ShoppingCart, Store, Briefcase, Monitor, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ErrorBoundary from './ErrorBoundary';
import InstallButton from './InstallButton';
import InstallPrompt from './InstallPrompt';
import { useDraftGuard } from '../context/DraftGuardContext';
import { useFeatures } from '../lib/useFeatures';
import { clearDocumentDraft } from '../lib/documentDraft';
import { fetchCountryCode, getCachedCountryCode } from '../lib/fiscal';
import SubscriptionDashboard from '../pages/SubscriptionDashboard';

export default function Layout({ children }) {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const changeLang = (lng) => { i18n.changeLanguage(lng); setShowLangMenu(false); };

  // Country-driven UI language. The app is Spain-first (es/ca), but a Pakistan
  // account operates in English (Urdu planned) — its users must never see the
  // Spanish/Catalan UI. We resolve the owner's fiscal country and, for PK, force
  // English and restrict the switcher to the languages that country offers, so
  // every already-translated page flips to English on load. ES keeps the full
  // es/en/ca choice untouched. Fails safe to ES on any error.
  // Seed from the last country this browser resolved so the switcher doesn't
  // flash the full es/en/ca menu on a PK account while the profile loads.
  const [fiscalCountry, setFiscalCountry] = useState(() => getCachedCountryCode());
  useEffect(() => {
    let active = true;
    fetchCountryCode()
      .then((code) => {
        if (!active) return;
        setFiscalCountry(code);
        if (code === 'PK' && (i18n.language || '').slice(0, 2) !== 'en') {
          // Force English IN-MEMORY ONLY: changeLanguage() makes the detector
          // cache 'en' in localStorage, which would stick for a later ES login
          // on this browser — so restore whatever was cached before the force.
          let prevCached = null;
          try { prevCached = localStorage.getItem('zbilling_lang'); } catch { /* ignore */ }
          i18n.changeLanguage('en').then(() => {
            try {
              if (prevCached === null) localStorage.removeItem('zbilling_lang');
              else localStorage.setItem('zbilling_lang', prevCached);
            } catch { /* ignore */ }
          });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [i18n]);
  // Languages the switcher offers for the active country. PK → English only
  // (Urdu 'ur' can be added here once its locale ships); everyone else → es/en/ca.
  const langOptions = fiscalCountry === 'PK' ? ['en'] : ['es', 'en', 'ca'];

  // Draft "form in progress" guard: intercept navigation away from a dirty create
  // page, and surface parked drafts through the notification bell.
  const { attemptNavigate, drafts, refreshDrafts } = useDraftGuard();
  const [showNotif, setShowNotif] = useState(false);
  // navigate that respects the leave guard, plus an optional menu-close side effect.
  const guardedGo = (path, close) => { if (close) close(); attemptNavigate(path); };

  // "Hoy a las 22:09" style timestamp for the activity feed.
  const formatDraftTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const time = d.toLocaleTimeString(i18n.language || 'es', { hour: '2-digit', minute: '2-digit' });
    const sameDay = new Date().toDateString() === d.toDateString();
    return sameDay ? `${t('draftGuard.today')} ${time}` : `${d.toLocaleDateString(i18n.language || 'es')} ${time}`;
  };

  // Super-admin impersonation: when set, we're viewing another account and show
  // a banner that lets us switch back to our own (admin) session.
  const [impersonation, setImpersonation] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('zb_impersonation') || 'null'); } catch { return null; }
  });
  const exitImpersonation = async () => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('zb_impersonation') || 'null'); } catch { saved = null; }
    sessionStorage.removeItem('zb_impersonation');
    setImpersonation(null);
    try {
      if (saved?.admin_refresh_token) {
        await supabase.auth.setSession({ access_token: saved.admin_access_token, refresh_token: saved.admin_refresh_token });
        window.location.href = '/admin';
        return;
      }
    } catch (e) { /* fall through to re-auth */ }
    window.location.href = '/auth';
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate('/auth');
      else setUser(session.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate('/auth');
      else setUser(session.user);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Guard against a silently-expired session: a token that can't be refreshed
  // would otherwise let the page sit there and fail on the next action. Check on
  // mount and whenever the tab regains focus, and re-auth gracefully if dead.
  useEffect(() => {
    let active = true;
    const check = async () => {
      const token = await getFreshToken();
      if (active && !token) await forceReauth();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    check();
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const [userRole, setUserRole] = useState('employee');
  // Extra permissions the owner granted this member on top of their role; lets a
  // nav item appear even when the base role wouldn't normally see it.
  const [extraPerms, setExtraPerms] = useState([]);
  const [planName, setPlanName] = useState('');
  const [subStatus, setSubStatus] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);
  const [subLoaded, setSubLoaded] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  // Per-client feature flags (SaaS module toggles). A nav item carrying a
  // `feature` key only shows when the current account has that module enabled.
  const { features: enabledFeatures } = useFeatures();

  useEffect(() => {
    if (user) {
      supabase.from('profiles').select('role, permissions, employer_id, subscription_plan_id, subscription_status, trial_end_date, account_status').eq('id', user.id).single()
        .then(async ({ data }) => {
          // The signup default role is 'user', which isn't part of the RBAC nav
          // roles — treat it (and a missing role) as the account owner.
          const r = data?.role;
          setUserRole(!r || r === 'user' ? 'business_owner' : r);
          setExtraPerms(Array.isArray(data?.permissions) ? data.permissions : []);
          // Subscription belongs to the company: a team member inherits their
          // employer's plan/status so access gating matches the business.
          let sub = data;
          if (data?.employer_id) {
            const { data: emp } = await supabase
              .from('profiles')
              .select('subscription_plan_id, subscription_status, trial_end_date, account_status')
              .eq('id', data.employer_id)
              .single();
            if (emp) sub = emp;
          }
          setSubStatus(sub?.subscription_status || null);
          setAccountStatus(sub?.account_status || null);
          setTrialEnd(sub?.trial_end_date || null);
          setSubLoaded(true);
          // Resolve the currently subscribed plan's name for display.
          if (sub?.subscription_plan_id) {
            const { data: plan } = await supabase.from('plans').select('name').eq('id', sub.subscription_plan_id).single();
            setPlanName(plan?.name || '');
          } else {
            setPlanName('');
          }
        });
    }
  }, [user]);

  // A gestoría lives in its own portal: confine it to /gestor and /settings so
  // it never lands on the client-side app (Tiendas, Facturas, dashboard…).
  useEffect(() => {
    if (userRole !== 'gestoría') return;
    const p = location.pathname;
    if (!(p.startsWith('/gestor') || p.startsWith('/settings'))) {
      navigate('/gestor', { replace: true });
    }
  }, [userRole, location.pathname, navigate]);

  const handleLogout = async () => {
    await logAudit('logout');
    await supabase.auth.signOut();
    navigate('/auth');
  };

  // group: 'primary' shows directly in the bar; 'more' goes under the "Más" dropdown.
  // The dashboard isn't a nav button — the "zBilling" logo links to it instead.
  const allNavItems = [
    { name: 'invoices', path: '/invoices', icon: FileText, group: 'primary', perm: 'billing.view', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'accountant'],
      isActiveFor: (loc) => loc.pathname.startsWith('/invoices') && !loc.search.includes('delivery_note') },
    { name: 'albaranes', path: '/invoices?doc=delivery_note', icon: ClipboardCheck, group: 'primary', perm: 'billing.view', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'accountant'],
      isActiveFor: (loc) => loc.pathname.startsWith('/invoices') && loc.search.includes('delivery_note') },
    { name: 'estimates', path: '/estimates', icon: ClipboardList, group: 'primary', perm: 'billing.view', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'expenses', path: '/expenses', icon: Receipt, group: 'primary', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'accountant', 'employee'] },
    { name: 'clients', path: '/clients', icon: Contact, group: 'primary', perm: 'clients.view', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'products', path: '/products', icon: Package, group: 'primary', perm: 'billing.view', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'orders', path: '/orders', icon: ShoppingCart, group: 'primary', perm: 'orders.view', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'comercial'] },
    { name: 'deliveries', path: '/deliveries', icon: Truck, group: 'primary', perm: 'delivery.view', roles: ['super_admin', 'admin', 'business_owner', 'repartidor'] },
    { name: 'payments', path: '/payments', icon: CreditCard, group: 'primary', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'reports', path: '/reports', icon: BarChart3, group: 'primary', roles: ['super_admin', 'admin', 'business_owner', 'accountant'] },
    // Material Management ERP — only shows when the client has the module enabled (SaaS feature flag).
    { name: 'materials', path: '/materials', icon: Boxes, group: 'primary', feature: 'material_management', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'employee'] },
    { name: 'tpv', path: '/tpv', icon: Monitor, group: 'more', perm: 'billing.create', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'stores', path: '/stores', icon: Store, group: 'more', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    // Tesorería now lives as a sub-tab inside Reports (Informe), so it's no longer a separate nav button.
    // IVA/AEAT tax models and SEPA remittances are Spain-only regulatory tools — hidden for PK accounts.
    { name: 'taxes', path: '/taxes', icon: Calculator, group: 'more', roles: ['super_admin', 'admin', 'business_owner', 'accountant', 'manager'], hideFor: ['PK'] },
    { name: 'banks', path: '/banks', icon: Building2, group: 'more', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'accountant'] },
    { name: 'remittances', path: '/remittances', icon: FileStack, group: 'more', roles: ['super_admin', 'admin', 'business_owner', 'manager', 'accountant'], hideFor: ['PK'] },
    { name: 'suppliers', path: '/suppliers', icon: Truck, group: 'more', roles: ['super_admin', 'admin', 'business_owner', 'manager'] },
    { name: 'gestor', path: '/gestor', icon: Briefcase, group: 'primary', roles: ['gestoría'] },
  ];

  // An item shows if the role normally has it, OR the owner granted the member
  // its permission as an extra (e.g. a comercial granted billing.view sees Facturas).
  // Items carrying a `feature` key are additionally gated on the client having
  // that module enabled (SaaS feature flag); ungated items are unaffected.
  const visibleItems = allNavItems.filter(item => {
    const roleOk = item.roles.includes(userRole) || (item.perm && extraPerms.includes(item.perm));
    if (!roleOk) return false;
    if (item.feature && !enabledFeatures.has(item.feature)) return false;
    if (item.hideFor && item.hideFor.includes(fiscalCountry)) return false;
    return true;
  });
  const primaryItems = visibleItems.filter(i => i.group === 'primary');
  const moreItems = visibleItems.filter(i => i.group === 'more');

  if (['admin', 'super_admin'].includes(userRole)) {
    moreItems.push({ name: 'admin', path: '/admin', icon: ShieldAlert });
    moreItems.push({ name: 'config', path: '/admin/billing', icon: Settings });
    moreItems.push({ name: 'gestores', path: '/admin/gestores', icon: Briefcase });
  }

  const moreActive = moreItems.some(i => i.isActiveFor ? i.isActiveFor(location) : location.pathname.startsWith(i.path));

  if (!user) return null;

  // Subscription gate: once the free trial is over (and the account isn't on an
  // active paid plan), the only thing left to do is activate a plan. Platform
  // admins and super-admin impersonation sessions are exempt so support can work.
  const isPlatformAdmin = ['admin', 'super_admin'].includes(userRole);
  const trialOver = subStatus === 'trial' && trialEnd && new Date(trialEnd) < new Date();
  // Locked when the trial ended, the subscription lapsed (expired/cancelled), or
  // billing suspended the account (unpaid renewal).
  const locked = subLoaded && !impersonation && !isPlatformAdmin && (
    trialOver
    || subStatus === 'suspended' || subStatus === 'expired' || subStatus === 'cancelled'
    || accountStatus === 'suspended'
  );

  if (locked) {
    return (
      <div className="min-h-screen bg-[#f8fafc] font-sans">
        {/* Minimal header — only the logo and a way out. No app navigation. */}
        <header className="app-header bg-white border-b border-[#e1e5e8] sticky top-0 z-50">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <span className="text-xl font-bold text-[#1a4f8b] tracking-tight">zBilling</span>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-[#1d2d3d] hidden sm:block">{user.email.split('@')[0]}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 text-red-600 hover:text-red-700 text-sm font-semibold bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors"
                >
                  <LogOut className="w-4 h-4" /> {t('usermenu.logout')}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Trial-ended banner */}
        <div className="bg-[#1a4f8b] text-white text-center py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium">
          <ShieldAlert className="w-4 h-4 text-[#fca311] flex-shrink-0" />
          {subStatus === 'suspended'
            ? t('layout.suspendedBanner')
            : t('layout.trialEndedBanner')}
        </div>

        <ErrorBoundary><SubscriptionDashboard /></ErrorBoundary>
      </div>
    );
  }

  // Days until the free trial ends; used for the site-wide expiry reminder that
  // starts 7 days before the account expires.
  const trialDaysLeft = subStatus === 'trial' && trialEnd
    ? Math.ceil((new Date(trialEnd) - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const showTrialReminder =
    subLoaded && trialDaysLeft !== null && trialDaysLeft >= 0 && trialDaysLeft <= 7
    && !location.pathname.startsWith('/subscription'); // Planes has its own banner

  return (
    <div className="min-h-screen bg-[#f4f7f9] font-sans">
      {/* Impersonation banner — visible while a super-admin is viewing another account */}
      {impersonation && (
        <div className="bg-purple-700 text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-3 relative z-[60]">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            <Trans
              i18n={i18n}
              i18nKey="layout.impersonatingBanner"
              values={{ name: impersonation.target_name || impersonation.target_email }}
              components={{ b: <b /> }}
            />
          </span>
          <button
            onClick={exitImpersonation}
            className="flex-shrink-0 bg-white/20 hover:bg-white/30 rounded-full px-3 py-1 font-bold transition-colors"
          >
            {t('layout.impersonationExit')}
          </button>
        </div>
      )}
      {/* Trial-expiry reminder — shown on every page during the last 7 days of trial */}
      {showTrialReminder && (
        <div className="bg-[#fca311] text-[#1d2d3d] text-sm font-semibold px-4 py-2 flex items-center justify-center gap-3 flex-wrap text-center">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <span>
            {trialDaysLeft === 0
              ? t('layout.trialEndsToday')
              : t('layout.trialEndsIn', {
                  count: trialDaysLeft,
                  days: trialDaysLeft,
                  date: new Date(trialEnd).toLocaleDateString(i18n.language || 'es-ES'),
                })}
            {' '}{t('layout.trialCta')}
          </span>
          <button
            onClick={() => guardedGo('/subscription')}
            className="flex-shrink-0 bg-[#1d2d3d] text-white hover:bg-[#1a4f8b] rounded-full px-4 py-1 font-bold transition-colors"
          >
            {t('layout.viewPlans')}
          </button>
        </div>
      )}
      {/* Top Navigation */}
      <header className="bg-white border-b border-[#e1e5e8] sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 gap-3">
            {/* Logo & Links */}
            <div className="flex min-w-0 flex-1">
              <button
                onClick={() => setShowMobileNav(true)}
                className="sm:hidden flex items-center justify-center -ml-1 mr-1 p-2 text-[#1a4f8b]"
                aria-label="Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => guardedGo('/dashboard')}
                className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                aria-label="Ir al panel"
                title="Ir al panel"
              >
                <span className="text-xl font-bold text-[#1a4f8b] tracking-tight">zBilling</span>
              </button>
              <nav className="hidden sm:flex sm:ml-4 space-x-3 overflow-x-auto whitespace-nowrap min-w-0 [&::-webkit-scrollbar]:hidden">
                {primaryItems.map((item) => {
                  const isActive = item.isActiveFor ? item.isActiveFor(location) : location.pathname.startsWith(item.path);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={(e) => { e.preventDefault(); guardedGo(item.path); }}
                      className={`inline-flex items-center flex-shrink-0 px-1 pt-1 border-b-[3px] text-sm font-semibold transition-colors ${
                        isActive
                          ? 'border-[#1a4f8b] text-[#1d2d3d]'
                          : 'border-transparent text-[#6d7d8b] hover:text-[#1a4f8b]'
                      }`}
                    >
                      <Icon className={`w-4 h-4 mr-1.5 flex-shrink-0 ${isActive ? 'text-[#1a4f8b]' : 'text-[#6d7d8b]'}`} />
                      {t('nav.' + item.name)}
                    </Link>
                  );
                })}
              </nav>

              {/* "Más" dropdown — sibling of <nav> so its menu isn't clipped by the nav's overflow */}
              {moreItems.length > 0 && (
                <div className="relative hidden sm:flex items-center flex-shrink-0 ml-3">
                  <button
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    className={`inline-flex items-center px-1 pt-1 border-b-[3px] text-sm font-semibold transition-colors ${
                      moreActive ? 'border-[#1a4f8b] text-[#1d2d3d]' : 'border-transparent text-[#6d7d8b] hover:text-[#1a4f8b]'
                    }`}
                  >
                    <MoreHorizontal className={`w-4 h-4 mr-1.5 ${moreActive ? 'text-[#1a4f8b]' : 'text-[#6d7d8b]'}`} />
                    {t('nav.more')}
                    <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${showMoreMenu ? 'rotate-180' : ''}`} />
                  </button>
                  {showMoreMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                      <div className="absolute left-0 top-full mt-2 w-56 bg-white border border-[#e1e5e8] rounded-xl shadow-xl py-1.5 z-50">
                        {moreItems.map((item) => {
                          const isActive = location.pathname.startsWith(item.path);
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={(e) => { e.preventDefault(); guardedGo(item.path, () => setShowMoreMenu(false)); }}
                              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                                isActive ? 'bg-blue-50 text-[#1a4f8b]' : 'text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b]'
                              }`}
                            >
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              {t('nav.' + item.name)}
                            </Link>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {/* Language switcher — hidden when the country offers a single
                  language (e.g. PK → English only), since there's nothing to pick. */}
              {langOptions.length > 1 && (
                <div className="relative">
                  <button onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-1 text-[#1a4f8b] hover:opacity-80 text-sm font-bold uppercase">
                    <Globe className="w-5 h-5" /> {(i18n.language || 'es').slice(0, 2)}
                  </button>
                  {showLangMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)}></div>
                      <div className="absolute right-0 mt-2 w-40 bg-white border border-[#e1e5e8] rounded-xl shadow-xl py-1 z-50">
                        {langOptions.map(lng => (
                          <button key={lng} onClick={() => changeLang(lng)}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 ${(i18n.language || '').startsWith(lng) ? 'text-[#1a4f8b] font-bold' : 'text-[#6d7d8b]'}`}>
                            {t('lang.' + lng)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <button className="text-[#1a4f8b] hover:opacity-80">
                <Headset className="w-5 h-5" />
              </button>
              {/* Notification bell — badges parked "forms in progress" (panel 7) */}
              <div className="relative">
                <button className="text-[#1a4f8b] hover:opacity-80 relative" onClick={() => setShowNotif(!showNotif)}>
                  <Bell className="w-5 h-5" />
                  {drafts.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#fca311] text-white text-[10px] font-bold flex items-center justify-center">
                      {drafts.length}
                    </span>
                  )}
                </button>
                {showNotif && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowNotif(false)} />
                    <div className="absolute right-0 mt-2 w-[340px] max-w-[90vw] bg-white border border-[#e1e5e8] rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-[#e1e5e8] bg-gray-50">
                        <p className="text-sm font-bold text-[#1d2d3d]">{t('draftGuard.activityTitle')}</p>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto">
                        {drafts.length === 0 ? (
                          <p className="px-4 py-8 text-center text-sm text-[#6d7d8b]">{t('draftGuard.noActivity')}</p>
                        ) : drafts.map((d) => (
                          <div key={d.storageKey} className="relative px-4 py-3 pr-9 border-b border-gray-100 last:border-0">
                            <button
                              onClick={() => { clearDocumentDraft(d.storageKey); refreshDrafts(); }}
                              title={t('draftGuard.dismissForm')}
                              className="absolute top-2.5 right-2.5 text-[#b6c0c8] hover:text-red-500 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <p className="text-[11px] text-[#9aa7b2]">{formatDraftTime(d.savedAt)}</p>
                            {d.clientName && (
                              <p className="text-[17px] font-bold text-[#1a4f8b] mt-0.5 leading-tight break-words">
                                {d.clientName}
                              </p>
                            )}
                            <p className="text-sm text-[#1d2d3d] mt-0.5">
                              {t('draftGuard.notifBody', { kind: t('draftGuard.kind_' + d.kindKey) })}
                            </p>
                            <button
                              onClick={() => {
                                setShowNotif(false);
                                const sep = d.resumePath.includes('?') ? '&' : '?';
                                guardedGo(`${d.resumePath}${sep}resume=1`);
                              }}
                              className="mt-2 inline-flex items-center px-3 py-1.5 rounded-full border border-[#1a4f8b] text-[#1a4f8b] text-xs font-bold hover:bg-blue-50 transition-colors"
                            >
                              {t('draftGuard.continueForm')}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <InstallButton className="hidden sm:flex items-center text-[#1a4f8b] font-bold text-sm bg-blue-50 px-3 py-1.5 rounded-full hover:bg-blue-100" />
              <button className="text-[#fca311] hover:text-orange-500 flex items-center font-bold text-sm bg-orange-100 px-3 py-1.5 rounded-full" onClick={() => guardedGo('/subscription')}>
                <Zap className="w-4 h-4 mr-1" /> Plan
              </button>
              
              {/* User Dropdown Menu */}
              <div className="relative">
                <div className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setShowUserMenu(!showUserMenu)}>
                  <span className="text-sm font-semibold text-[#1d2d3d] mr-1 hidden sm:block">{user.email.split('@')[0]}</span>
                  <div className="h-8 w-8 rounded-full bg-[#fca311] flex items-center justify-center text-white font-bold text-sm tracking-widest shadow-sm">
                    {user.email.substring(0, 2).toUpperCase()}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#6d7d8b] transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </div>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)}></div>
                    <div className="absolute right-0 mt-2 w-64 bg-white border border-[#e1e5e8] rounded-xl shadow-xl py-2 z-50 overflow-hidden">
                      <div className="px-5 py-3 border-b border-[#e1e5e8] bg-gray-50">
                        <p className="text-sm font-bold text-[#1d2d3d] truncate">{user.email.split('@')[0]}</p>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className="inline-block px-2 py-0.5 border border-orange-400 text-orange-500 text-[10px] font-bold rounded uppercase tracking-wider bg-orange-50">{t('usermenu.owner')}</span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-300 text-[#1a4f8b] text-[10px] font-bold rounded uppercase tracking-wider bg-blue-50">
                            <Zap className="w-3 h-3" /> {planName || t('usermenu.noPlan')}
                          </span>
                        </div>
                      </div>
                      <div className="py-1">
                        <Link to="/settings/business" className="block px-5 py-2.5 text-sm text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b] font-medium transition-colors" onClick={(e) => { e.preventDefault(); guardedGo('/settings/business', () => setShowUserMenu(false)); }}>{t('usermenu.business')}</Link>
                        <Link to="/settings/account" className="block px-5 py-2.5 text-sm text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b] font-medium transition-colors" onClick={(e) => { e.preventDefault(); guardedGo('/settings/account', () => setShowUserMenu(false)); }}>{t('usermenu.account')}</Link>
                        {['business_owner', 'admin'].includes(userRole) && (
                           <Link to="/settings/team" className="block px-5 py-2.5 text-sm text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b] font-medium transition-colors" onClick={(e) => { e.preventDefault(); guardedGo('/settings/team', () => setShowUserMenu(false)); }}>{t('usermenu.team')}</Link>
                        )}
                        <Link to="/settings/customization" className="block px-5 py-2.5 text-sm text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b] font-medium transition-colors" onClick={(e) => { e.preventDefault(); guardedGo('/settings/customization', () => setShowUserMenu(false)); }}>{t('usermenu.customization')}</Link>
                        {['business_owner', 'admin'].includes(userRole) && (
                          <Link to="/subscription" className="flex items-center justify-between px-5 py-2.5 text-sm text-[#6d7d8b] hover:bg-blue-50 hover:text-[#1a4f8b] font-medium transition-colors" onClick={(e) => { e.preventDefault(); guardedGo('/subscription', () => setShowUserMenu(false)); }}>
                            <span>{t('usermenu.plans')}</span>
                            <span className="text-[11px] font-bold text-[#1a4f8b]">{planName || t('usermenu.noPlan')}</span>
                          </Link>
                        )}
                      </div>
                      <div className="border-t border-[#e1e5e8] my-1"></div>
                      <button onClick={handleLogout} className="block w-full text-left px-5 py-2.5 text-sm text-red-600 hover:bg-red-50 font-medium transition-colors">
                        {t('usermenu.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile slide-in navigation */}
      {showMobileNav && (
        <div className="sm:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileNav(false)} />
          <div className="absolute left-0 top-0 h-full w-[78%] max-w-[320px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-5 h-16 border-b border-[#e1e5e8]">
              <button type="button" onClick={() => guardedGo('/dashboard', () => setShowMobileNav(false))} className="text-xl font-bold text-[#1a4f8b] tracking-tight" aria-label="Ir al panel">zBilling</button>
              <button onClick={() => setShowMobileNav(false)} className="p-2 text-[#6d7d8b]" aria-label="Cerrar">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {[...primaryItems, ...moreItems].map((item) => {
                const isActive = item.isActiveFor ? item.isActiveFor(location) : location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={(e) => { e.preventDefault(); guardedGo(item.path, () => setShowMobileNav(false)); }}
                    className={`flex items-center gap-3 px-5 py-3.5 text-[15px] font-semibold transition-colors ${
                      isActive ? 'bg-blue-50 text-[#1a4f8b] border-l-4 border-[#1a4f8b]' : 'text-[#1d2d3d] hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-[#1a4f8b]' : 'text-[#6d7d8b]'}`} />
                    {t('nav.' + item.name)}
                  </Link>
                );
              })}
            </nav>
            <div className="m-4 space-y-2">
              <InstallButton className="w-full flex items-center justify-center text-[#1a4f8b] font-bold text-sm bg-blue-50 px-3 py-2.5 rounded-full hover:bg-blue-100" />
              <button onClick={() => guardedGo('/subscription', () => setShowMobileNav(false))} className="w-full text-[#fca311] flex items-center justify-center font-bold text-sm bg-orange-100 px-3 py-2.5 rounded-full">
                <Zap className="w-4 h-4 mr-1" /> Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="max-w-[1600px] mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      {/* Mobile "install this app" banner */}
      <InstallPrompt />
    </div>
  );
}
